import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import Authorities from "../../../../auth/authorities";
import withAuthority from "../../../../components/Auth/withAuthority";
import {
  createStyles,
  Grid,
  Hidden,
  makeStyles,
  Theme,
} from "@material-ui/core";
import GoogleMap from "../../../../components/common/googleMap.tsx";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_UPDATED,
} from "../../../../utils/consts";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import { getObjectDifferences } from "../../../../utils/isDeepEqual";
import {
  getIntegrationConfig,
  getIntegrationsPerLocation,
} from "../../../../services/locationApp/integrationsService";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import IntegrationsInfoNode from "./IntegrationsInfoNode";

export interface BasicInfoProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addressInfoStyle: {
      paddingRight: "12px",
      [theme.breakpoints.down("xs")]: {
        paddingRight: "0px",
      },
    },
    googleMapStyle: {
      paddingLeft: "12px",
      [theme.breakpoints.down("xs")]: {
        paddingLeft: "0px",
        marginTop: "8px",
      },
    },
  }),
);

/* Get all basic information and location information using API call at initial load. */
const Integrations: React.FunctionComponent<BasicInfoProps> = () => {
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [integrationConfig, setIntegrationConfig] = useState("");
  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;

  const getAllIntegrationsPerLocation = async () => {
    try {
      const response = await getIntegrationsPerLocation(locationId);
      if (response.status === 200) {
        const data = response.data;
        // Handle response whether it's wrapped in { integrations: [...] } or direct array [...]
        const integrationsList = Array.isArray(data)
          ? data
          : data?.integrations || [];

        setIntegrations(integrationsList);
      }
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
    setIsLoading(false);
  };
  const displayName = localStorage.getItem("businessDisplayName");
  // const autherizationRequest = async (clientId: any) => {
  //   try {
  //     const authUrl = `https://manager.hubrise.com/oauth2/v1/authorize?client_id=${clientId}.clients.hubrise.com&redirect_uri=https://demo.locations.prestoexpress.co.uk/7713dce7-51ec-4870-b251-4bce1c9249f8/presto-locations&response_type=code&scope=location[orders.write,catalog.write]&country=GB&location_name=${displayName}}`;

  //     // Open in new tab
  //     window.open(authUrl, "_blank", "noopener,noreferrer");

  //     // Alternative: You can also use window.open without the second parameter for default new tab behavior
  //     // window.open(authUrl);
  //   } catch (err) {
  //     setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
  //   }
  //   setIsLoading(false);
  // };

  // const getAllIntegrationConfig = async () => {
  //   try {
  //     const response = await getIntegrationConfig(locationId);
  //     if (response.status === 200) {
  //       const data = response.data;
  //       // Handle response whether it's wrapped in { integrations: [...] } or direct array [...]
  //       autherizationRequest(data?.client_id);
  //       setIntegrationConfig(data);
  //     }
  //   } catch (err) {
  //     setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
  //   }
  //   setIsLoading(false);
  // };

  // Injects a minimal loading message into the still-blank tab so the user
  // doesn't stare at a plain white page while we wait for the API call.
  // Safe to call here because the tab is still same-origin (about:blank) —
  // this stops working the moment we navigate it to a cross-origin URL.
  const showLoadingMessage = (win: Window) => {
    try {
      win.document.write(`
      <!DOCTYPE html>
      <html>
        <head><title>Connecting to HubRise…</title></head>
        <body style="margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:sans-serif;color:#555;">
          <p>Connecting to HubRise, please wait…</p>
        </body>
      </html>
    `);
      win.document.close();
    } catch (e) {
      // Non-critical — if this fails for any reason, the user just sees a
      // blank tab for a moment instead of a message. Not worth surfacing.
    }
  };

  const autherizationRequest = (clientId: any, windowRef: Window | null) => {
    try {
      const authUrl = `https://manager.hubrise.com/oauth2/v1/authorize?client_id=${clientId}.clients.hubrise.com&redirect_uri=https://demo.locations.prestoexpress.co.uk/7713dce7-51ec-4870-b251-4bce1c9249f8/presto-locations/integrations/redirect&response_type=code&scope=location[orders.write,catalog.write]&country=GB&location_name=${displayName}`;

      if (windowRef) {
        // Redirect the blank tab we already opened to the real auth URL
        windowRef.location.href = authUrl;

        // Sever the window.opener link on the child AFTER navigating it to
        // HubRise's cross-origin URL. Per spec, `opener` is one of the few
        // properties a parent can still write cross-origin, so this call
        // succeeds even though the tab now points at manager.hubrise.com.
        // This protects against reverse tabnabbing: without this, a
        // compromised or malicious page in that tab could use
        // `window.opener.location = "..."` to silently redirect this
        // Presto Locations tab (the parent) to a phishing page.
        try {
          // @ts-ignore - `opener` is writable per spec even cross-origin
          windowRef.opener = null;
        } catch (e) {
          // Some older browsers may not support this — safe to ignore.
        }
      } else {
        // windowRef is null (popup was blocked) — try opening a new tab as a
        // last resort. Note: this call happens after an `await`, so Safari
        // may block it too. This is only a fallback, not a guarantee.
        window.open(authUrl, "_blank");
      }
    } catch (err) {
      if (windowRef) windowRef.close(); // Close the blank tab if an error occurred
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
    // setIsLoading(false) intentionally NOT called here — the caller
    // (getAllIntegrationConfig) owns loading state and sets it exactly once,
    // in its `finally` block, so we don't fire duplicate state updates.
  };

  const getAllIntegrationConfig = async () => {
    // 1. Open a blank tab the instant the user clicks, before any `await`
    //    (this is what bypasses Safari's popup blocker). Don't pass
    //    noopener/noreferrer — doing so makes window.open() return null,
    //    which means we'd lose the reference and couldn't set its location
    //    or opener later.
    const windowRef = window.open("about:blank", "_blank");

    // 2. Don't assume the popup always succeeds — let the user know
    //    immediately instead of waiting for the API round-trip to fail.
    if (!windowRef) {
      setError(
        "Your browser blocked the HubRise connection window. Please allow pop-ups for this site and try again.",
      );
      setIsLoading(false);
      return;
    }

    // 3. Show a friendly loading message in the tab while we wait for the
    //    API response, instead of a blank white page.
    showLoadingMessage(windowRef);

    try {
      const response = await getIntegrationConfig(locationId);
      if (response.status === 200) {
        const data = response.data;
        autherizationRequest(data?.client_id, windowRef);
        setIntegrationConfig(data);
      } else {
        // Bug fix: this branch used to close the tab silently, with no
        // feedback to the user at all. Now it surfaces the same error the
        // catch block would.
        windowRef.close();
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    } catch (err) {
      windowRef.close();
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      // Single source of truth for loading state — avoids the double-call
      // that existed when both this function and autherizationRequest each
      // called setIsLoading(false).
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllIntegrationsPerLocation();
  }, []);

  console.log("integrationConfig", integrationConfig);
  const classes = useStyles();
  console.log("integrations", integrations);

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>
      <IntegrationsInfoNode
        isLoading={isLoading}
        integrations={integrations}
        getAllIntegrationConfig={getAllIntegrationConfig}
        getAllIntegrationsPerLocation={getAllIntegrationsPerLocation}
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(Integrations, Authorities.BASIC_INFO_READ);
